import { CalendarDensity, CalendarMode } from "@clockwise/web-commons/src/components/calendar";
import useIsNarrow from "@clockwise/web-commons/src/hooks/useIsNarrow";
import { useUpdateActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import {
  useReadCalendar,
  useUpdateCalendar,
} from "@clockwise/web-commons/src/util/CalendarContext";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { useEcosystem } from "@clockwise/web-commons/src/util/ecosystem";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { uniq, without } from "lodash";
import { DateTime } from "luxon";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocalStorage } from "usehooks-ts";
import { addCalendarIds, removeCalendarIds } from "../../../state/actions/multi-calendar.actions";
import { IReduxState } from "../../../state/reducers/root.reducer";
import { useUpdateActiveEventDiff } from "../../chat-plus-calendar/util/ActiveDiffContext";
import { useAIMessageContext } from "../../chat/ai-chat/hooks/AIMessageContext";
import { useUpdateSuggestedPrompt } from "../../chat/ai-chat/hooks/SuggestedPromptContext";
import { TradeoffsPopover } from "../../tradeoffs-popover";
import { usePlannerContext } from "../Context";
import PlannerToolbarRender from "./PlannerToolbarRender";
import { useHighlightCalendarStepNavigation } from "./hooks/useHighlightCalendarStepNavigation";

export const plannerToolbarSettingKeys = {
  unFadePastEvents: "unFadePastEvents",
};

export const PlannerToolbar = ({
  showCalendarPeek = false,
  singleDay = false,
}: {
  showCalendarPeek?: boolean;
  singleDay?: boolean;
}) => {
  const { shouldExcludeAi } = useAIMessageContext();
  const updatePromptText = useUpdateSuggestedPrompt();
  const setActiveEvent = useUpdateActiveEvent();
  const setActiveDiff = useUpdateActiveEventDiff();

  const {
    calendarError: error,
    calendarDataLoading: loading,
    calendarRef,
    hideDeclined,
    setHideDeclined,
    selectedCalendarIds: userCalendarIds = [],
    calendarIds: allCalendarIds = [],
  } = usePlannerContext();
  const calendarIdsForCalendarPeek = useSelector(
    (state: IReduxState) => state.multiCalendar.calendarIdsForCalendarPeek,
  );

  const dispatch = useDispatch();
  const ecosystem = useEcosystem();

  const { focusedDate, visibleDates, weekendsShown } = useReadCalendar();
  const calendarDispatch = useUpdateCalendar();
  const track = useTracking();
  const compact = useIsNarrow(calendarRef);
  const [density, setCalendarDensity] = useLocalStorage<CalendarDensity>(
    "calendarDensity",
    "default",
  );
  const zone = getRenderTimeZone();
  const {
    navButtonToHighlight,
    tradeoffs,
    showTradeoffs,
    currentTimeSuggestionString,
    totalAttendeeCount,
  } = useHighlightCalendarStepNavigation(visibleDates, zone);

  // LocalStorage settings are only created on user interaction
  const [showHolds, setShowHolds] = useLocalStorage<boolean>("showHolds", false);
  // Set the defaulted state such that past events are faded for those who have not
  // interacted with the toggle
  const [unFadePastEvents, setUnFadePastEvents] = useLocalStorage<boolean>(
    plannerToolbarSettingKeys.unFadePastEvents,
    false,
  );

  const errorMessage = error ? "Something went wrong loading your calendar." : undefined;

  const mode = visibleDates.length > 1 ? "week" : "day";

  const currentDateTime = DateTime.fromISO(focusedDate, { zone: getRenderTimeZone() });

  const handleTodayClick = () =>
    calendarDispatch({
      type: mode === "day" ? "jumpTo-date" : "jumpTo-weekOf",
      payload: DateTime.now().toISODate(),
    });

  const handleCalendarModeChange = (mode: CalendarMode) => {
    calendarDispatch({
      type: "set-view",
      payload: { view: mode, date: focusedDate },
    });
  };

  const handleNavigateToDate = (date: string) => {
    calendarDispatch({
      type: mode === "day" ? "jumpTo-date" : "jumpTo-weekOf",
      payload: date,
    });
  };

  const handleShiftNext = () => calendarDispatch({ type: "step-forward" });

  const handleShiftPrevious = () => calendarDispatch({ type: "step-backward" });

  const handleShowDeclinedEventsChange = (checked: boolean) => {
    const hideDeclined = !checked;
    track(TrackingEvents.PLANNER.SETTINGS.HIDE_DECLINED, { hidden: hideDeclined });
    setHideDeclined?.(hideDeclined);
  };

  const handleShowWeekendsChange = (checked: boolean) => {
    track(TrackingEvents.PLANNER.SETTINGS.HIDE_WEEKENDS, { hidden: !checked });
    calendarDispatch({ type: "set-weekendsShown", payload: checked });
  };

  const handleFadePastEventsChange = (faded: boolean) => {
    track(TrackingEvents.PLANNER.SETTINGS.FADE_PAST_EVENTS, { faded });
    setUnFadePastEvents(!faded);
  };

  const handleCalendarDensityChange = (density: CalendarDensity) => {
    // TODO: this does not belong in calendar reducer
    track(TrackingEvents.PLANNER.SETTINGS.DENSITY, { density });
    setCalendarDensity(density);
  };

  const handleShowHoldsChange = (collapse: boolean) => {
    track(TrackingEvents.PLANNER.SETTINGS.COLLAPSE_HOLDS, { collapse });
    setShowHolds(collapse);
  };

  const handleMultiCalAdd = (calendarIds: string[]) => dispatch(addCalendarIds(calendarIds));
  const handleMultiCalRemove = (calendarIds: string[]) => dispatch(removeCalendarIds(calendarIds));

  const handleFindTime = () => {
    const text = "Schedule @";
    updatePromptText(text);
    setActiveEvent(null);
    setActiveDiff(null);
    track(TrackingEvents.CHAT.NUX.CHAT_RECOMMENDATION_CLICKED, { text });
  };

  // DevNote: selectedCalendarIds and additionalCalendarIds in PlannerContext are currently broken; this will work around the issue until it can be resolved.
  // TODO: https://linear.app/getclockwise/issue/AI-3028/fix-calendarid-tracking-in-planner-context
  const selectedCalendarIds = without(allCalendarIds, ...userCalendarIds);

  const getDateFormat = () => {
    if (singleDay) {
      return compact ? "MMM DD YYYY" : "ddd, MMMM DD YYYY";
    }
    return compact ? "MMM YYYY" : "MMMM YYYY";
  };
  const HighlightedNavButtonTooltip = ({
    anchorEl,
  }: {
    anchorEl: React.RefObject<HTMLDivElement>;
  }) => {
    return (
      <TradeoffsPopover
        tradeoffs={tradeoffs}
        side="bottom"
        totalAttendeeCount={totalAttendeeCount}
        anchorEl={anchorEl}
        headerText={currentTimeSuggestionString ?? undefined}
      />
    );
  };

  return (
    <PlannerToolbarRender
      calendarPeek={showCalendarPeek}
      currentDate={currentDateTime.toISODate()}
      dateFormat={getDateFormat()}
      declinedEventsEnabled={!hideDeclined}
      density={density || "default"}
      ecosystem={ecosystem}
      errorMessage={errorMessage}
      fadePastEvents={!unFadePastEvents}
      highlightedNavButtonTooltip={showTradeoffs ? HighlightedNavButtonTooltip : null}
      loading={!!loading}
      mode={mode}
      navButtonToHighlight={navButtonToHighlight}
      onCalendarDensityChange={handleCalendarDensityChange}
      onCalendarModeChange={handleCalendarModeChange}
      onFadePastEventsChange={handleFadePastEventsChange}
      onFindTime={shouldExcludeAi ? undefined : handleFindTime}
      onMultiCalAdd={handleMultiCalAdd}
      onMultiCalRemove={handleMultiCalRemove}
      onNavigateToDate={handleNavigateToDate}
      onShiftNext={handleShiftNext}
      onShiftPrevious={handleShiftPrevious}
      onShowDeclinedEventsChange={handleShowDeclinedEventsChange}
      onShowHoldsChange={handleShowHoldsChange}
      onShowWeekendsChange={handleShowWeekendsChange}
      onTodayClick={handleTodayClick}
      previewedCalendarId={uniq(calendarIdsForCalendarPeek)}
      selectedCalendarIds={selectedCalendarIds || []}
      showHolds={showHolds}
      weekendsEnabled={weekendsShown}
    />
  );
};

export default PlannerToolbar;
